import { createReducer, on } from '@ngrx/store';
import { SaveDraftItem } from '@shared/components/save-draft-modal/save-draft-modal.model';
import { RetrieveDraftItem } from '@shared/services/draft/draft.service';
import {
  ActiveDraftChangedSuccess,
  DeleteDrafts,
  DeleteDraftsFailure,
  DeleteDraftsStarted,
  DeleteDraftsSuccess,
  DraftLookupFailureAcknowledged,
  DraftResumeAccepted,
  DraftResumeDeclined,
  HydrateDraftFailure,
  HydrateDraftSuccess,
  MinorDraftLookupTimedOut,
  RetrieveDrafts,
  RetrieveDraftsFailure,
  RetrieveDraftsSuccess,
  SaveNewDraftSuccess,
  StaleDraftDiscarded,
} from '@store/actions/draft/draft.actions';

/**
 * `draftId` cannot serve this purpose — it is null on every flow entry, so "not looked yet",
 * "found nothing" and "lookup failed" would be indistinguishable, and flow entry could not
 * tell whether creating a draft is safe.
 */
export enum DraftLookupState {
  NOT_CHECKED = 'NOT_CHECKED',
  CHECKING = 'CHECKING',
  NONE_FOUND = 'NONE_FOUND',
  AWAITING_DECISION = 'AWAITING_DECISION',
  /** Distinct from DECLINED: treating this as settled orphans a draft, since draftId is
   * still null until hydration supplies it. */
  RESUMED = 'RESUMED',
  DECLINED = 'DECLINED',
  /** The lookup failed. Separate from NONE_FOUND so we can say "we couldn't load your saved
   * request" rather than "you don't have one". */
  LOOKUP_FAILED = 'LOOKUP_FAILED',
  /** The client accepted the prompt and loading the draft then failed. Separate from
   * LOOKUP_FAILED so we don't tell them the lookup failed when it didn't. */
  RESUME_FAILED = 'RESUME_FAILED',
}

export interface DraftState {
  draftId: string | null;
  baseProcessTrackingId: string | null;
  draftSequence: number;
  expDate: string | null;
  drafts: SaveDraftItem[];
  rawDrafts: RetrieveDraftItem[];
  draftsLoading: boolean;
  lookupState: DraftLookupState;
  lookupFailureAcknowledged: boolean;
}

export const initialState: DraftState = {
  draftId: null,
  baseProcessTrackingId: null,
  draftSequence: 0,
  expDate: null,
  drafts: [],
  rawDrafts: [],
  draftsLoading: false,
  lookupState: DraftLookupState.NOT_CHECKED,
  lookupFailureAcknowledged: false,
};

export const draftReducer = createReducer(
  initialState,
  on(SaveNewDraftSuccess, (state, { draftId, expDate, baseProcessTrackingId, draftSequence }) => ({
    ...state,
    draftId,
    baseProcessTrackingId,
    draftSequence,
    expDate,
  })),
  on(ActiveDraftChangedSuccess, (state, { draftId, draftSequence }) => ({
    ...state,
    draftId,
    draftSequence,
  })),
  on(HydrateDraftSuccess, (state, { draftId, baseProcessTrackingId, draftSequence }) => ({
    ...state,
    draftId,
    baseProcessTrackingId,
    draftSequence,
  })),
  on(RetrieveDrafts, (state) => ({
    ...state,
    drafts: [],
    rawDrafts: [],
    draftsLoading: true,
    lookupState: DraftLookupState.CHECKING,
    lookupFailureAcknowledged: false,
  })),
  on(RetrieveDraftsSuccess, (state, { drafts, rawDrafts }) => ({
    ...state,
    drafts,
    rawDrafts,
    draftsLoading: false,
    lookupState: drafts.length ? DraftLookupState.AWAITING_DECISION : DraftLookupState.NONE_FOUND,
  })),
  on(RetrieveDraftsFailure, (state) => ({
    ...state,
    drafts: [],
    rawDrafts: [],
    draftsLoading: false,
    lookupState: DraftLookupState.LOOKUP_FAILED,
  })),
  // Same outcome as RetrieveDraftsFailure: notice + fresh-draft gate.
  on(MinorDraftLookupTimedOut, (state) => ({
    ...state,
    drafts: [],
    rawDrafts: [],
    draftsLoading: false,
    lookupState: DraftLookupState.LOOKUP_FAILED,
  })),
  on(DraftResumeAccepted, (state) => ({
    ...state,
    lookupState: DraftLookupState.RESUMED,
  })),
  // Without a settled state here the client is stranded: RESUMED is not "settled", so a failed
  // hydration leaves the create gate shut and the prompt already latched closed — an empty form,
  // no error, no way forward. RESUME_FAILED falls back to start-fresh, and being distinct from
  // LOOKUP_FAILED keeps the failed-lookup notice off a client whose resume is what failed.
  on(HydrateDraftFailure, (state) => ({
    ...state,
    lookupState: DraftLookupState.RESUME_FAILED,
  })),
  on(DraftResumeDeclined, (state) => ({
    ...state,
    lookupState: DraftLookupState.DECLINED,
  })),
  // Only the dismissal — lookupState still remembers that the lookup failed.
  on(DraftLookupFailureAcknowledged, (state) => ({
    ...state,
    lookupFailureAcknowledged: true,
  })),
  on(DeleteDrafts, (state, { ids }) =>
    state.draftId && ids.includes(state.draftId)
      ? { ...state, draftId: null, baseProcessTrackingId: null, draftSequence: 0, expDate: null }
      : state,
  ),
  on(DeleteDraftsStarted, (state) => ({
    ...state,
    draftsLoading: true,
  })),
  on(DeleteDraftsSuccess, (state) => ({
    ...state,
    draftsLoading: false,
  })),
  on(DeleteDraftsFailure, (state) => ({
    ...state,
    draftsLoading: false,
  })),
  on(StaleDraftDiscarded, (state) => ({
    ...state,
    drafts: [],
    rawDrafts: [],
    lookupState: DraftLookupState.NONE_FOUND,
  })),
);
